import { Fragment, useState, useEffect } from "react";
import { ScrollRestoration, useSearchParams } from "react-router-dom";
import LoadingPlaceholder from "../../components/ui/loading/LoadingPlaceholder";

function TermsConditions({ embed = false }) {
  const [searchParams] = useSearchParams();
  const isEmbed = embed || searchParams.get("embed") === "1";
  const [loading, setLoading] = useState(true);

  // Simulate loading for consistency and future API calls
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 600);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Fragment>
      <ScrollRestoration />
      <main className={isEmbed ? "" : "legal-page"}>
        <div className={isEmbed ? "" : "content legal-content"}>
          {loading ? (
            <LoadingPlaceholder rows={3} type="card-large" />
          ) : (
            <div id="terms">
              {!isEmbed && <h1>Terms & Conditions</h1>}
              <p>Date of Last Revision: October 15, 2025</p>
              <h3>1. Contractual Relationship</h3>
              <p>
                These Plaintr Terms and Conditions (“Terms”) govern your access
                to and use of the Plaintr platform and related services,
                including websites, mobile applications, content, products, and
                services provided by Plaintr LLC (“Plaintr,” “we,” or “us”)
                within the United States. By accessing or using the Plaintr
                Services, you agree to be bound by these Terms, forming a
                legally binding contract with Plaintr. If you do not agree to
                these Terms, you may not access or use the Plaintr Services.
              </p>
              <h4>1.1. Scope of These Terms</h4>
              <p>
                The Plaintr Services connect users (“Users”) seeking tax
                preparation and advisory services with verified Pros, including
                Certified Public Accountants (“CPAs”), Enrolled Agents (“EAs”),
                and Tax Attorneys (collectively, “Pros”). Plaintr acts solely as
                an intermediary to facilitate these connections and does not
                provide tax or accounting services itself. Any services provided
                by Pros are governed by separate agreements between Users and
                Pros.
              </p>
              <h4>1.1.1 Definitions</h4>
              <p>
                “Pro(s)” means certified CPAs, Enrolled Agents, or Tax Attorneys
                who provide services via Plaintr’s platform. “IRP” means the
                Internal Resolution Panel, a three-member review body (including
                a compliance officer and a senior CPA/EA) that evaluates
                disputes and refund claims before arbitration.
              </p>
              <h4>1.2. Acceptance of Terms</h4>
              <p>
                By creating an Account, continuing with Google, Apple, or
                another supported authentication provider, logging into the
                Plaintr Services, submitting information through the Platform,
                or otherwise accessing or using the Plaintr Services, you
                acknowledge that you have read, understood, and agree to be
                bound by these Terms and the Privacy Policy. If you do not agree
                to these Terms, you must not create an Account or use the
                Plaintr Services.
              </p>
              <h4>1.3. Scope of These Terms</h4>
              <p>
                Supplemental terms may apply to certain services, such as
                privacy practices or promotions. These supplemental terms will
                be provided in connection with the applicable services and are
                incorporated by reference into these Terms. In the event of a
                conflict between these Terms and supplemental terms, the
                supplemental terms shall govern.
              </p>
              <h4>1.4. Acceptance of Terms</h4>
              <p>
                Plaintr reserves the right to revise these Terms periodically.
                If material changes occur that may significantly impact your
                rights, Plaintr will notify you by email or platform updates.
                Unless specified otherwise, changes are effective immediately
                upon posting. Your continued use of the platform constitutes
                acceptance of these changes.
              </p>
              <h4>1.5. Privacy Policy</h4>
              <p>
                Plaintr is committed to protecting your privacy. Our practices
                for collecting and using personal information are outlined in
                our Privacy Policy. By accessing the Plaintr Services, you
                consent to the handling of your personal information as
                described in our Privacy Policy.
              </p>
              <h3>2. Services Overview and Eligibility</h3>
              <h4>2.1 Scope of Services</h4>
              <p>
                Plaintr provides a digital platform connecting Users with
                verified Pros. The platform facilitates engagement and
                interaction but does not directly provide tax preparation or
                advisory services. By accessing or using Plaintr, you agree that
                the platform serves as an intermediary to streamline your
                connection with Pros, and any services provided through these
                connections are outside Plaintr’s control and responsibility.
              </p>
              <h4>2.2 Categories of Pros</h4>
              <p>Plaintr’s network of Pros includes:</p>
              <ol>
                <li>
                  <strong>Certified Public Accountants (CPAs):</strong> Experts
                  in accounting and tax preparation.
                </li>
                <li>
                  <strong>Enrolled Agents (EAs):</strong> Licensed professionals
                  authorized by the IRS to represent taxpayers.
                </li>
              </ol>
              <p>
                Each Pro operates independently and is solely responsible for
                the services they provide to Users and can offer Tax Services as
                an individual or as part of a company or organization.
              </p>
              <h4>2.3 Limitations of Service</h4>
              <p>
                Plaintr solely offers a platform that facilitates matching,
                connection, and interaction between Users and Pros. Plaintr does
                not:
              </p>
              <ol>
                <li>
                  Partake in the tax preparation process or verify the accuracy
                  of information exchanged between Users and Pros.
                </li>
                <li>
                  Guarantee the suitability, reliability, or ability of Tax
                  Professionals beyond the algorithmic matching process.
                </li>
              </ol>
              <p>
                Users acknowledge that the success of the matching process
                depends on the completeness and accuracy of the information they
                provide.
              </p>
              <h4>2.4 Non-Liability for Pro Services</h4>
              <p>
                Any engagement, advice, or service provided by a Pro is governed
                by a separate contractual relationship directly between the User
                and the Pro. Plaintr bears no liability for:
              </p>
              <ol>
                <li>
                  The quality, accuracy, or completeness of the Pro’s work.
                </li>
                <li>
                  Any disputes arising between Users and Pros regarding services
                  rendered.
                </li>
                <li>
                  Potential errors in tax filings or advice resulting from the
                  Tax Professional’s work.
                </li>
              </ol>
              <h4>2.5. Pro Matching and Independence</h4>
              <ol>
                <li>
                  <strong>Algorithmic Matching:</strong> Plaintr uses a
                  proprietary algorithm to match Users with Pros based on
                  criteria such as tax situation complexity, industry, and
                  location preferences (non-exhaustive list). While Plaintr
                  strives to provide a suitable match, no guarantees are made
                  regarding a Pro’s ability to meet all User expectations.
                </li>
                <li>
                  <strong>User Rights:</strong> Users have the right to request
                  a different Pro if dissatisfied with the match, subject to
                  platform policies and availability.
                </li>
              </ol>
              <h4>2.6. Modifications and Updates</h4>
              <p>
                Plaintr reserves the right to modify the functionality of the
                platform, including updates to tools or features, at its
                discretion. Major updates impacting core functionality will be
                communicated to Users.
              </p>
              <h4>2.7. Eligibility Requirements</h4>
              <h5>2.7.1 User Eligibility</h5>
              <p>To use the Plaintr Services, Users must:</p>
              <ol>
                <li>
                  Be at least 18 years old or the age of majority in their
                  jurisdiction.
                </li>
                <li>Be a resident of the United States.</li>
                <li>
                  Provide accurate, current, and complete information during
                  registration.
                </li>
                <li>Comply with all applicable laws and regulations.</li>
              </ol>
              <h5>2.7.2Pro Eligibility</h5>
              <p>Pros using the platform must:</p>
              <ol>
                <li>Hold valid and current licenses or certifications.</li>
                <li>
                  Comply with all applicable laws, regulations, and professional
                  standards governing their practice.
                </li>
                <li>
                  Provide accurate and complete professional credentials and
                  agree to periodic verification by Plaintr.
                </li>
                <li>
                  Merit the favorable exercise of discretion by the Plaintr
                  team.
                </li>
              </ol>
              <h3>3. User Accounts</h3>
              <h4>3.1. Account Creation</h4>
              <p>
                To access certain features of the Plaintr Services, Users must
                register for an account
                <strong>(“Account”)</strong> and provide the necessary
                information during the registration process. Users agree to:
              </p>
              <ol>
                <li>
                  <strong>Accurate Information:</strong> Provide accurate,
                  current, and complete information during registration.
                </li>
                <li>
                  <strong>Updates:</strong> Promptly update their Account
                  information to ensure accuracy and completeness.
                </li>
                <li>
                  <strong>Professional Credentials (Pros):</strong> Pros must
                  provide valid credentials and agree to periodic verification
                  as required by Plaintr.
                </li>
              </ol>
              <h4>3.2. Account Security</h4>
              <p>
                Users are responsible for maintaining the confidentiality of
                their login credentials and for all activities conducted through
                their Account. Users agree to:
              </p>
              <ol>
                <li>
                  Use a strong password and keep their login credentials
                  confidential.
                </li>
                <li>
                  Notify Plaintr immediately of any unauthorized use of their
                  Account or any other security breach.
                </li>
                <li>Log out of their Account at the end of each session.</li>
              </ol>
              <p>
                Plaintr is not liable for any loss or damage arising from a
                User’s failure to comply with these requirements.
              </p>
              <h4>3.3. Account Responsibilities</h4>
              <p>Users agree to:</p>
              <ol>
                <li>
                  <strong>Truthful Representation:</strong> Not impersonate any
                  person or entity or misrepresent their identity or
                  affiliation.
                </li>
                <li>
                  <strong>Non-Transferability:</strong> Not transfer or assign
                  their Account to any other person or entity without prior
                  written consent from Plaintr.
                </li>
                <li>
                  <strong>Compliance:</strong> Use the platform in accordance
                  with applicable laws, regulations, and these Terms.
                </li>
              </ol>
              <h4>3.4. Prohibited Conduct</h4>
              <p>The following activities are strictly prohibited:</p>
              <h5>Fraudulent Activities:</h5>
              <ol>
                <li>
                  Providing false, misleading, or inaccurate information during
                  registration or service engagement.
                </li>
                <li>Misrepresenting qualifications or credentials.</li>
              </ol>
              <h5>Unauthorized Access:</h5>
              <ol>
                <li>
                  Attempting to gain unauthorized access to any portion of the
                  platform, other User accounts, or Plaintr’s systems.
                </li>
                <li>Using another User’s Account without permission.</li>
              </ol>
              <h5>Automated Access and Data Scraping:</h5>
              <ol>
                <li>
                  Using automated tools (e.g., bots, spiders) to scrape data
                  from the platform.
                </li>
                <li>
                  Attempting to disrupt the operation of the platform through
                  malicious code, excessive load, or interference.
                </li>
              </ol>
              <h5>Illegal Activities:</h5>
              <ol>
                <li>
                  Engaging in or promoting activities that are illegal under
                  applicable laws.
                </li>
              </ol>
              <h5>Harmful Conduct:</h5>
              <ol>
                <li>
                  Uploading, posting, or transmitting unlawful, harmful,
                  threatening, or objectionable content.
                </li>
                <li>
                  Harassing, threatening, or otherwise harming other Users or
                  Tax Professionals.
                </li>
              </ol>
              <h4>3.5. Data Responsibility (Pros)</h4>
              <p>Pros are responsible for:</p>
              <ol>
                <li>
                  Ensuring the accuracy, validity, and completeness of their
                  professional credentials and license information.
                </li>
                <li>
                  Promptly updating their Account to reflect changes in their
                  professional status, including suspension, revocation, or
                  expiration of licenses.
                </li>
                <li>
                  Complying with periodic credential verification requests from
                  Plaintr.
                </li>
              </ol>
              <h4>3.6. Termination of Account</h4>
              <p>
                Plaintr reserves the right to suspend or terminate a User’s
                Account for violations of these Terms or prohibited conduct.
                Upon termination:
              </p>
              <ol>
                <li>
                  Access Revocation: Users will lose access to their Account and
                  associated services.
                </li>
                <li>
                  Outstanding Obligations: Users remain responsible for
                  resolving any outstanding fees or disputes.
                </li>
                <li>
                  Data Retention: Plaintr may retain User data as required by
                  law or for legitimate business purposes, in accordance with
                  the Privacy Policy.
                </li>
              </ol>
              <h3>4. Payment Terms and Fees</h3>
              <h4>4.1. Payment Processing</h4>
              <p>
                Payments for services provided by Pros are processed through a
                secure third-party payment processor (the{" "}
                <strong>“Payment Processor”</strong>), such as Stripe. Users
                acknowledge and agree that:
              </p>
              <ol>
                <li>
                  <strong>Secure Processing:</strong> Payment information,
                  including credit or debit card details, is processed securely
                  by the Payment Processor and not stored by Plaintr.
                </li>
                <li>
                  <strong>Authorization:</strong> By initiating a payment, Users
                  authorize the Payment Processor to charge their payment method
                  for the agreed amounts.
                </li>
                <li>
                  <strong>Processor Liability:</strong> Plaintr is not
                  responsible for errors, delays, or disputes related to the
                  Payment Processor. Users must address such issues directly
                  with the processor.
                </li>
              </ol>
              <h4>4.2. Platform Fees</h4>
              <p>
                Plaintr charges a service fee (<strong>“Platform Fee”</strong>)
                for facilitating the connection between Users and Tax
                Professionals. This fee:
              </p>
              <ol>
                <li>
                  <strong>Disclosure:</strong> Is disclosed to both the User and
                  Pro before any transaction is initiated.
                </li>
                <li>
                  <strong>Non-Refundable:</strong> Is non-refundable unless
                  explicitly stated otherwise, required by law, or otherwise
                  stated in these Terms.
                </li>
              </ol>
              <h4>4.3. Payment Responsibilities</h4>
              <h5>4.3.1. For Users:</h5>
              <ol>
                <li>
                  Accurate Payment Information: Provide accurate and up-to-date
                  payment details.
                </li>
                <li>
                  Resolve Payment Issues: Address any declined or disputed
                  payments promptly.
                </li>
                <li>
                  Consequences of Non-Payment: Accept that non-payment may
                  result in suspension or termination of Account access.
                </li>
              </ol>
              <h5>4.3.2 For Pros:</h5>
              <ol>
                <li>
                  Fee Deductions: Payments to Pros for completed services are
                  issued after deducting the Platform Fee.
                </li>
                <li>
                  Payment Schedule: Payment schedules and methods may vary and
                  are subject to the Payment Processor’s terms.
                </li>
                <li>
                  Tax Obligations: Pros are responsible for reporting and paying
                  applicable taxes on income earned through the platform.
                </li>
              </ol>
              <h4>4.4. Refunds and Adjustments</h4>
              <h4>4.4.1 Holding of Funds</h4>
              <p>
                All client payments made through the Platform are subject to a
                <strong>secure payment hold</strong> administered through
                Plaintr’s payment processing infrastructure. Funds are securely
                held and are not released to the assigned tax professional until
                the applicable completion conditions set forth in these Terms
                are satisfied.
              </p>
              <p>
                Plaintr does not act as an escrow agent and does not provide
                escrow services. The secure payment hold is a technical and
                contractual mechanism designed to ensure proper completion of
                services, resolution of disputes if applicable, and compliance
                with Platform requirements.
              </p>
              <p>
                Funds may remain on hold in cases including, but not limited to,
                incomplete services, unresolved disputes, verification issues,
                or violations of these Terms.
              </p>
              <h4>4.4.2 Refund Policy Tiers</h4>
              <p>Refunds, if applicable, are governed as follows:</p>
              <ol>
                <strong>Eligibility</strong> — A Client may be entitled to a
                refund if:
                <ul>
                  <li>No work was performed despite Client cooperation.</li>
                  <li>
                    Services contained material errors or omissions affecting
                    the Client’s tax position, as confirmed by the IRP.
                  </li>
                  <li>
                    The Pro breached confidentiality, SLA, or the Service
                    Agreement.
                  </li>
                  <li>
                    Plaintr made a matching error (assigning a Pro without valid
                    credentials).
                  </li>
                </ul>
              </ol>
              <ol>
                <strong>Exclusions</strong> — Refunds are NOT available if:
                <ul>
                  <li>The Client fails to provide required information.</li>
                  <li>
                    Work was delivered as agreed but the Client changed their
                    mind.
                  </li>
                  <li>IRS/state outcomes were outside the Pro’s control.</li>
                  <li>
                    Requests are submitted after 30 days of service
                    completion/deadline.
                  </li>
                </ul>
              </ol>
              <ol>
                <strong>Process</strong> —
                <ul>
                  <li>
                    Requests must be submitted via dashboard or
                    refunds@Plaintr.com within 30 calendar days.
                  </li>
                  <li>
                    If linked to a dispute, the IRP reviews; if not, Plaintr
                    Support reviews.
                  </li>
                  <li>A decision is issued within 10 business days.</li>
                  <li>Approved refunds are issued within 5 business days.</li>
                </ul>
              </ol>
              <h4>4.4.3 Payment Schedule & Withholding</h4>
              <p>
                Pros are paid weekly on Wednesdays via Stripe for all completed
                cases. Stripe’s standard processing times (2–3 business days)
                apply. The Platform Fee is charged to the Client and does not
                reduce the Pro’s agreed earnings. Plaintr may withhold payouts
                in cases of SLA breach, dispute, or incomplete work, until
                resolution is reached.
              </p>
              <h4>4.5. Taxes and Withholding</h4>
              <p>Users and Pros acknowledge that</p>
              <ol>
                <li>
                  <strong>Pros’ Responsibility:</strong> Tax Professionals are
                  solely responsible for reporting and paying applicable taxes
                  on income earned through the platform.
                </li>
                <li>
                  <strong>Plaintr’s Responsibility:</strong> Plaintr is not
                  responsible for calculating, collecting, or remitting taxes on
                  behalf of Users or Pros.
                </li>
                <li>
                  <strong>Sales Tax or VAT:</strong> Any applicable sales tax or
                  value-added tax (VAT) on Platform Fees will be disclosed and
                  charged during transactions.
                </li>
                <li>
                  <strong>No Tax Remittance by Plaintr:</strong> Plaintr does
                  not calculate or remit taxes but holds the payment for the Tax
                  Professional Fee until service completion.
                </li>
              </ol>
              <h4>4.6. Fee Changes</h4>
              <p>
                Plaintr reserves the right to modify Platform Fees at any time.
                Changes will be communicated to Users at least fourteen (14)
                days before implementation. Continued use of the platform
                constitutes acceptance of the updated fees.
              </p>
              <h4>4.7. Disputes Related to Fees</h4>
              <ol>
                <li>
                  Dispute Timeline: Users must contact Plaintr’s support team
                  within thirty (30) days of a disputed charge to raise a
                  concern.
                </li>
                <li>
                  Waiver of Rights: Failure to dispute fees within this timeline
                  waives the right to contest the charge.
                </li>
                <li>
                  Service Disputes: Disputes involving services rendered by Tax
                  Professionals must be addressed directly with the Tax
                  Professional, as outlined in Section 6.
                </li>
              </ol>
              <h3>5. Use of the Plaintr Services</h3>
              <h4>5.1. Authorized Use</h4>
              <p>
                Plaintr grants Users a limited, non-exclusive, non-transferable,
                and revocable license to access and use the Plaintr platform for
                its intended purpose: facilitating connections between Users and
                Pros. Users agree to:
              </p>
              <ol>
                <li>
                  <strong>Lawful Purposes:</strong> Use the platform solely for
                  lawful purposes.
                </li>
                <li>
                  <strong>Compliance:</strong> Abide by all applicable laws,
                  regulations, and these Terms when using the platform.
                </li>
                <li>
                  <strong>Accurate Information:</strong> Provide truthful and
                  accurate information during registration and when engaging
                  with the Plaintr Services.
                </li>
              </ol>
              <h4>5.2. Prohibited Conduct</h4>
              <p>The following actions are strictly prohibited:</p>
              <h5>Fraudulent Use:</h5>
              <ol>
                <li>
                  Providing false, misleading, or inaccurate information during
                  registration or engagement.
                </li>
                <li>Misrepresenting qualifications or credentials.</li>
              </ol>
              <h5>Unauthorized Access:</h5>
              <ol>
                <li>
                  Attempting to gain unauthorized access to Plaintr systems,
                  other User accounts, or private data.
                </li>
                <li>Using another User’s Account without permission.</li>
              </ol>
              <h5>Data Scraping and Automation:</h5>
              <ol>
                <li>
                  Employing automated tools, such as bots or scrapers, to
                  collect data from the platform.
                </li>
                <li>
                  Disrupting the platform’s operation through excessive load,
                  malicious code, or interference.
                </li>
              </ol>
              <h5>Illegal Activities:</h5>
              <ol>
                <li>
                  Using the platform to engage in activities that are illegal
                  under applicable laws.
                </li>
              </ol>
              <h5>Harmful Conduct:</h5>
              <ol>
                <li>
                  Uploading, posting, or transmitting harmful, defamatory, or
                  objectionable content.
                </li>
                <li>
                  Harassing, threatening, or causing harm to other Users or Tax
                  Professionals.
                </li>
              </ol>
              <h4>5.3. Intellectual Property</h4>
              <p>
                Plaintr owns all intellectual property rights associated with
                its platform, including content, software, logos, and
                trademarks. Users agree to:
              </p>
              <ol>
                <li>
                  <strong>Restrictions:</strong> Not reproduce, distribute, or
                  create derivative works based on Plaintr’s intellectual
                  property without explicit permission.
                </li>
                <li>
                  <strong>Software Integrity:</strong> Not reverse-engineer or
                  decompile Plaintr’s software.
                </li>
                <li>
                  <strong>Third-Party Rights:</strong> Respect the intellectual
                  property rights of third parties, including Pros.
                </li>
              </ol>
              <h4>5.4. Platform Modifications and Availability</h4>
              <p>Plaintr reserves the right to:</p>
              <ol>
                <li>
                  <strong>Modify Services:</strong> Make updates, modifications,
                  or discontinue any aspect of the platform at its discretion.
                </li>
                <li>
                  <strong>Service Interruptions:</strong> Temporarily interrupt
                  services for maintenance, technical issues, or other reasons.
                  Plaintr is not liable for losses resulting from such
                  interruptions.
                </li>
                <li>
                  <strong>Advance Notice:</strong> Notify Users in advance of
                  significant changes affecting the platform’s functionality.
                </li>
              </ol>
              <h4>5.5. User Responsibilities</h4>
              <p>Users are responsible for:</p>
              <ol>
                <li>
                  <strong>Account Security:</strong> Maintaining the
                  confidentiality of their login credentials and account
                  information.
                </li>
                <li>
                  <strong>Information Accuracy:</strong> Keeping their account
                  details accurate and up to date.
                </li>
                <li>
                  <strong>Compliance:</strong> Ensuring their use of the
                  platform adheres to these Terms and applicable laws.
                </li>
              </ol>
              <p>
                Pros are additionally responsible for ensuring the accuracy and
                legality of the services they provide through the platform.
              </p>
              <h4>5.6. Monitoring and Enforcement</h4>
              <p>Plaintr reserves the right to:</p>
              <ol>
                <li>
                  <strong>Content Removal:</strong> Remove or modify content
                  that violates these Terms.
                </li>
                <li>
                  <strong>Account Actions:</strong> Suspend or terminate
                  accounts for non-compliance.
                </li>
                <li>
                  <strong>Legal Reporting:</strong> Report unlawful conduct to
                  authorities if necessary.
                </li>
              </ol>
              <h4>5.7. Feedback and Suggestions</h4>
              <p>
                Users may provide feedback or suggestions to improve Plaintr
                Services. By doing so, Users agree that:
              </p>
              <ol>
                <li>
                  <strong>Usage Rights:</strong> Plaintr may use such feedback
                  without obligation or compensation.
                </li>
                <li>
                  <strong>No Ownership Rights:</strong> Submitting feedback does
                  not create intellectual property rights for the User.
                </li>
              </ol>
              <h3>6. Pro Relationships and Disclaimers</h3>
              <h4>6.1. Independent Contractors</h4>
              <p>
                Pros listed on the Plaintr platform are independent contractors.
                They are not employees, agents, or representatives of Plaintr.
                Users acknowledge that:
              </p>
              <ol>
                <li>
                  <strong>Independent Operation:</strong> Pros operate
                  independently and are solely responsible for the services they
                  provide.
                </li>
                <li>
                  <strong>Plaintr’s Role:</strong> Plaintr’s role is limited to
                  facilitating the matching, connection, and interaction between
                  Users and Pros.
                </li>
              </ol>
              <h4>6.1.1 Service Level Agreement (SLA)</h4>
              <ol>
                <li>
                  Pros must respond to new Client messages within one (1)
                  business hour.
                </li>
                <li>
                  Pros must respond to ongoing Client messages within twelve
                  (12) business hours.
                </li>
                <li>
                  Pros must provide proactive updates at least once every two
                  (2) business days if no Client activity occurs.
                </li>
                <li>
                  All official case updates, deliverables, and important
                  communications must be documented in the Plaintr dashboard.
                </li>
              </ol>
              <h4>6.1.2 Off-Platform Rules</h4>
              <p>
                Client communication may occur off-platform when necessary, but
                all payments and deliverables must be processed exclusively
                through Plaintr. Any engagement outside the platform is
                prohibited unless approved in writing by Plaintr.
              </p>
              <h4>6.2. Scope of Responsibility</h4>
              <p>
                Plaintr’s responsibilities are limited to providing a platform
                for Users and Pros to connect. Plaintr does not:
              </p>
              <ol>
                <li>
                  Participate in or oversee the preparation of tax returns or
                  other tax-related services.
                </li>
                <li>
                  Verify or guarantee the accuracy of advice or documents
                  provided by Pros beyond the initial credential verification.
                </li>
                <li>
                  Assume liability for any outcomes, errors, or omissions
                  resulting from services provided by Pros.
                </li>
              </ol>
              <h4>6.3. Non-Guarantee of Results</h4>
              <p>
                Plaintr does not guarantee the quality, accuracy, or reliability
                of services provided by Pros. Users agree that:
              </p>
              <ol>
                <li>
                  <strong>Outcome Dependence:</strong> The outcome of tax
                  services depends on the information provided by the User and
                  the professional judgment of the Pro.
                </li>
                <li>
                  <strong>No Warranties:</strong> Plaintr makes no express or
                  implied warranties regarding the results of services provided
                  through the platform.
                </li>
              </ol>
              <h4>6.4. Disputes Between Users and Pros</h4>
              <p>
                Disputes arising between Users and Pros regarding services
                rendered will be addressed and resolved by Plaintr. Plaintr’s
                role in resolving disputes includes:
              </p>
              <ol>
                <li>
                  <strong>Facilitation:</strong> Providing support to facilitate
                  communication and assess the details of the dispute, including
                  reviewing messages, forms, and other interactions conducted
                  through the platform.
                </li>
                <li>
                  <strong>Funds Withholding:</strong> Withholding funds
                  temporarily while the dispute is under review.
                </li>
                <li>
                  <strong>Resolution Determination:</strong> Acting as an
                  arbitrator to determine the appropriate resolution. For
                  example:
                  <ol>
                    <li>
                      If the User’s complaint is justified, Plaintr may:
                      <ol>
                        <li>Reassign the User to a new Pro.</li>
                        <li>
                          Issue a full refund to the User, in which case the
                          original Pro will not receive payment.
                        </li>
                      </ol>
                    </li>
                    <li>
                      If the Pro’s actions are deemed appropriate, Plaintr will
                      release the withheld funds to the Tax Professional.
                    </li>
                  </ol>
                </li>
              </ol>
              <p>
                Plaintr’s decisions in dispute resolution are binding and aim to
                ensure fairness and accountability for all parties.
              </p>
              <h4>6.4.1 Dispute Submission</h4>
              <ol>
                <li>
                  Disputes must be submitted via the Plaintr dashboard or by
                  email to disputes@Plaintr.com.
                </li>
                <li>
                  Submissions must include case ID, reason, and supporting
                  evidence.
                </li>
              </ol>
              <h4>6.4.2 Evidence Rules</h4>
              <ol>
                <li>
                  Only records stored in the Plaintr dashboard (messages,
                  documents, deliverables, payments) are automatically
                  admissible.
                </li>
                <li>
                  Off-platform evidence may be accepted only if pre-approved in
                  writing.
                </li>
                <li>
                  Each party has three (3) business days to submit evidence;
                  late submissions are accepted only if they contain material
                  new evidence.
                </li>
              </ol>
              <h4>6.4.3 Resolution Timeline</h4>
              <ol>
                <li>
                  Standard disputes: IRP issues a decision within ten (10)
                  business days of acknowledgment.
                </li>
                <li>
                  Urgent disputes (e.g., filing deadlines within five (5) days):
                  IRP issues a decision within three (3) business days.
                </li>
              </ol>
              <h4>6.4.4 Possible Outcomes</h4>
              <ol>
                <li>Full refund</li>
                <li>Partial refund</li>
                <li>Corrective work at no cost</li>
                <li>Reassignment to a new Pro</li>
                <li>Full payment release if Pro’s work is validated</li>
              </ol>
              <h4>6.4.5 Appeals</h4>
              <ol>
                <li>
                  Either party may appeal only if new, material evidence exists.
                </li>
                <li>
                  Appeals must be submitted within five (5) business days of the
                  IRP decision.
                </li>
                <li>Appeal decisions are final within the Plaintr process.</li>
              </ol>
              <h4>6.4.6 Confidentiality</h4>
              <p>
                All disputes, evidence, and outcomes are confidential, except
                where disclosure is required by law.
              </p>
              <h4>6.5. Verification of Credentials</h4>
              <p>
                While Plaintr verifies the professional credentials of Tax
                Professionals during onboarding and conducts periodic checks,
                such verification is limited. Users acknowledge that:
              </p>
              <ol>
                <li>
                  <strong>Limited Verification:</strong> Plaintr verifies the
                  validity of licenses or certifications at the time of
                  onboarding but does not conduct ongoing monitoring of
                  performance or compliance.
                </li>
                <li>
                  <strong>User Due Diligence:</strong> Users are responsible for
                  ensuring the Pro they engage meets their specific needs and
                  qualifications.
                </li>
              </ol>
              <h4>6.6. Limitation of Liability for Pro Services</h4>
              <p>Plaintr is not liable for:</p>
              <ol>
                <li>
                  <strong>Professional Errors:</strong> Errors, omissions, or
                  misinterpretations made by Pros.
                </li>
                <li>
                  <strong>Financial Losses:</strong> Financial losses,
                  penalties, or damages arising from services provided by Pros.
                </li>
                <li>
                  <strong>Expectation Failure:</strong> A Pro failing to meet a
                  User’s expectations.
                </li>
              </ol>
              <h3>7. Limitation of Liability</h3>
              <h4>7.1. General Limitation of Liability</h4>
              <p>
                To the fullest extent permitted by law, Plaintr’s liability to
                Users for any claim arising out of or related to the use of the
                platform is limited to the amount of the Platform Fees paid by
                the User for the transaction giving rise to the claim. Users
                agree that:
              </p>
              <ol>
                <li>
                  <strong>No Indirect Damages:</strong> Plaintr is not liable
                  for indirect, incidental, special, consequential, or punitive
                  damages, including but not limited to lost profits, lost
                  revenue, or business interruptions, even if Plaintr has been
                  advised of the possibility of such damages.
                </li>
                <li>
                  <strong>Liability Cap:</strong> The total liability of
                  Plaintr, whether in contract, tort, or otherwise, will not
                  exceed the fees paid by the User for the specific service in
                  question.
                </li>
              </ol>
              <h4>7.2. Exclusions of Liability</h4>
              <p>
                Plaintr is not responsible for damages or losses resulting from:
              </p>
              <ol>
                <li>
                  <strong>Services Provided by Pros:</strong>
                  <ol>
                    <li>
                      Errors, omissions, or inaccuracies in tax filings or
                      advice.
                    </li>
                    <li>Misconduct or negligence by Pros.</li>
                    <li>Disputes between Users and Pros.</li>
                  </ol>
                </li>
                <li>
                  <strong>User Actions:</strong>
                  <ol>
                    <li>
                      Incorrect or incomplete information submitted by the User.
                    </li>
                    <li>
                      Misuse of the platform, including violations of these
                      Terms.
                    </li>
                  </ol>
                </li>
                <li>
                  <strong>Third-Party Systems:</strong>
                  <ol>
                    <li>
                      Errors, delays, or failures by Payment Processors or other
                      third-party services.
                    </li>
                    <li>
                      Security breaches caused by third-party systems outside
                      Plaintr’s control.
                    </li>
                  </ol>
                </li>
                <li>
                  <strong>Service Interruptions:</strong>
                  <ol>
                    <li>
                      Temporary unavailability of the platform due to
                      maintenance, technical issues, or force majeure events
                      (e.g., natural disasters, government actions, or internet
                      outages).
                    </li>
                  </ol>
                </li>
              </ol>
              <h4>7.3. Indemnification</h4>
              <p>
                Users agree to indemnify, defend, and hold harmless Plaintr, its
                affiliates, employees, and agents from and against any claims,
                liabilities, damages, losses, or expenses (including reasonable
                attorney’s fees) arising from:
              </p>
              <ol>
                <li>
                  Violation of Terms: The User’s violation of these Terms or
                  applicable laws.
                </li>
                <li>
                  User Conduct: Actions or omissions by the User resulting in
                  harm to another User or Pro.
                </li>
                <li>
                  Service Claims: Claims related to services provided by Tax
                  Professionals, except in cases where Plaintr’s negligence
                  directly caused the issue.
                </li>
              </ol>
              <h4>7.4. No Warranties</h4>
              <p>
                Plaintr provides its services on an “as-is” and “as-available”
                basis. To the fullest extent permitted by law, Plaintr disclaims
                all warranties, express or implied, including but not limited
                to:
              </p>
              <ol>
                <li>Merchantability: Warranties of merchantability.</li>
                <li>
                  Fitness for Purpose: Warranties of fitness for a particular
                  purpose.
                </li>
                <li>Non-Infringement: Warranties of non-infringement.</li>
                <li>
                  Service Expectations: Warranties that the platform will meet
                  Users’ requirements, operate without interruption, or be
                  error-free.
                </li>
              </ol>
              <h4>7.5. Release of Claims</h4>
              <p>
                By using the platform, Users release Plaintr from any claims,
                demands, or damages arising out of disputes with Pros or other
                Users, except in cases where Plaintr has breached its
                obligations under these Terms.
              </p>
              <h4>7.6. Jurisdictional Limitations</h4>
              <p>
                Some jurisdictions may not allow the exclusion or limitation of
                certain liabilities or damages. In such cases, Plaintr’s
                liability will be limited to the fullest extent permitted by
                law.
              </p>
              <h4>7.7. Force Majeure</h4>
              <p>
                Plaintr is not liable for any failure or delay in performance
                caused by circumstances beyond its reasonable control, including
                but not limited to acts of God, labor disputes, natural
                disasters, or disruptions in telecommunications.
              </p>
              <h3>8. Dispute Resolution and Arbitration</h3>
              <h4>8.1. Binding Arbitration</h4>
              <p>
                Any disputes, claims, or controversies arising out of or
                relating to these Terms, the Plaintr Services, or any
                interactions facilitated by the platform shall be resolved
                through binding arbitration. By agreeing to these Terms, Users
                waive their right to a trial by jury or to participate in a
                class action.
              </p>
              <h5>8.1.1. Arbitration Rules</h5>
              <ol>
                <li>
                  <strong>Governing Rules:</strong> Arbitration will be
                  conducted under the rules of the American Arbitration
                  Association (“AAA”) or another mutually agreed-upon
                  arbitration service.
                </li>
                <li>
                  <strong>Location:</strong> The arbitration will take place in
                  New York, NY, unless both parties agree to an alternative
                  location.
                </li>
              </ol>
              <h5>8.1.2. Arbitration Costs</h5>
              <ol>
                <li>
                  <strong>Cost Allocation:</strong> Each party shall bear its
                  own costs and expenses for arbitration, except as required by
                  applicable law or arbitration rules.
                </li>
                <li>
                  <strong>Financial Hardship:</strong> If a User demonstrates
                  financial hardship, Plaintr may contribute to the arbitration
                  fees at its discretion.
                </li>
              </ol>
              <h5>8.1.3. IRP Requirement</h5>
              <p>
                Before arbitration, all disputes must first be submitted to the
                IRP process described in Section 6.4. Only after the IRP issues
                a decision may either party escalate to arbitration.
              </p>
              <h5>8.1.4. Submission Rules</h5>
              <p>
                Disputes for arbitration must be filed through the Plaintr
                dashboard or emailed to disputes@Plaintr.com, including
                supporting evidence.
              </p>
              <h5>8.1.5. Finality of Decision</h5>
              <p>
                Binding Decision: The arbitrator’s decision will be final and
                binding on both parties and enforceable in any court of
                competent jurisdiction.
              </p>
              <p>
                This decision follows IRP review before arbitration. Appeals to
                AAA are permitted only as described in Section 6.4, and
                arbitration decisions are final under the Federal Arbitration
                Act.
              </p>
              <h4>8.2. Class Action Waiver</h4>
              <p>
                Users agree that disputes will be resolved on an individual
                basis and not as part of any class, collective, or
                representative action.
              </p>
              <ol>
                <li>
                  <string>No Consolidation:</string> Arbitration will not be
                  consolidated with any other proceeding involving another
                  party.
                </li>
                <li>
                  <strong>Waiver of Representative Claims:</strong> Users
                  expressly waive their right to bring representative claims on
                  behalf of others.
                </li>
              </ol>
              <h4>8.3. Exceptions to Arbitration</h4>
              <p>
                Despite the arbitration agreement, the following actions may be
                pursued in court:
              </p>
              <ol>
                <li>
                  <strong>Small Claims:</strong> Disputes that qualify for
                  resolution in small claims court in the User’s jurisdiction.
                </li>
                <li>
                  <strong>Injunctive Relief:</strong> Plaintr may seek
                  injunctive or equitable relief in a court to protect its
                  intellectual property or prevent misuse of the platform.
                </li>
              </ol>
              <h4>8.4. Governing Law</h4>
              <p>
                These Terms and any disputes arising out of them shall be
                governed by and construed in accordance with the laws of the
                State of New York, without regard to its conflict of law
                provisions.
              </p>
              <h4>8.5. Opt-Out Procedure</h4>
              <p>
                Users may opt out of the arbitration agreement by providing
                written notice to Plaintr within thirty (30) days of first
                accepting these Terms. The notice must include:
              </p>
              <ol>
                <li>
                  User Information: The User’s full name and contact information
                </li>
                <li>
                  Statement of Intent: A clear statement of intent to opt out of
                  arbitration.
                </li>
                <li>
                  Delivery: The written notice must be sent to{" "}
                  <a href="mailto:contact@Plaintr.com">contact@Plaintr.com</a>{" "}
                  or via certified mail to:{" "}
                  <strong>228 Park Ave S #554715, New York, NY 10003</strong>
                </li>
              </ol>
              <p>
                Opting out of arbitration does not affect other provisions of
                these Terms, including the class action waiver.
              </p>
              <h4>8.6. Survival</h4>
              <p>
                This dispute resolution and arbitration section shall survive
                the termination of these Terms and the termination of a User’s
                relationship with Plaintr.
              </p>
              <h3>9. Termination</h3>
              <h4>9.1. User-Requested Account Deletion</h4>
              <p>Users may terminate their Account at any time by:</p>
              <ol>
                <li>
                  Users may request deletion of their Account through Account
                  Settings by selecting “Request Account Deletion.” Users may
                  also contact Plaintr at{" "}
                  <a href="mailto:contact@Plaintr.com">contact@Plaintr.com</a>{" "}
                  for assistance. Submission of a request does not result in
                  immediate deletion. Plaintr may verify the User’s identity and
                  review the Account before processing the request.
                </li>
                <li>
                  If a tax return has been prepared or filed for the User
                  through Plaintr, the Account and related tax records may be
                  subject to applicable IRS and other legal retention
                  requirements. While the required retention period remains in
                  effect, the Account cannot be deleted and will remain
                  accessible to the User, unless access is separately suspended
                  or administratively terminated under these Terms.
                </li>
                <li>
                  If the Account is not subject to an applicable retention
                  requirement, the deletion request will be completed following
                  Plaintr’s review. If a retention period applies, Plaintr will
                  complete the deletion request after that period expires.
                </li>
                <li>
                  Once deletion is completed, the Account and associated
                  personal information will be permanently deleted, except for
                  limited information that must be retained for a longer period
                  as required or permitted by law. The User will permanently
                  lose access to the Account and the information available
                  through it. A deleted Account cannot be recovered or
                  reactivated.
                </li>
              </ol>
              <h4>9.2. Plaintr’s Right to Terminate</h4>
              <p>
                Plaintr reserves the right to suspend or terminate User Accounts
                at its sole discretion for any of the following reasons:
              </p>
              <ol>
                <li>
                  Violation of Terms: Breach of these Terms, including
                  unauthorized use of the platform or prohibited conduct as
                  outlined in Section 5.
                </li>
                <li>
                  Fraudulent Activity: Suspected fraud, misuse, or illegal
                  activities associated with the User’s Account.
                </li>
                <li>
                  Non-Payment: Failure to resolve outstanding fees or disputes
                  related to payments.
                </li>
                <li>
                  Platform Integrity: Actions by the User that compromise the
                  security, integrity, or functionality of the platform.
                </li>
              </ol>
              <p>
                Termination under these circumstances may occur with or without
                prior notice to the User.
              </p>
              <h4>9.3. Effects of Termination</h4>
              <p>
                Upon Account termination, whether initiated by the User or
                Plaintr:
              </p>
              <ol>
                <li>
                  Access Revocation: Users will lose access to their Account and
                  any associated services, data, or content.
                </li>
                <li>
                  Outstanding Transactions: Plaintr may process any pending
                  payments or transactions before closing the Account. Users
                  remain responsible for resolving disputes or obligations
                  related to Pros.
                </li>
                <li>
                  Data Retention: Plaintr reserves the right to retain User data
                  as required by law or for legitimate business purposes, as
                  outlined in the Privacy Policy.
                </li>
                <li>
                  No Refunds: Termination does not entitle the User to a refund
                  of fees already paid unless explicitly stated otherwise in
                  these Terms.
                </li>
              </ol>
              <h4>9.4. Reactivation of Accounts</h4>
              <p>
                Plaintr may, at its sole discretion, allow terminated Accounts
                to be reactivated under specific conditions. Users seeking
                reactivation must:
              </p>
              <ol>
                <li>
                  Formal Request: Submit a formal request via email to
                  support@Plaintr.com.
                </li>
                <li>
                  Issue Resolution: Address the cause of termination, such as
                  resolving outstanding issues or disputes.
                </li>
              </ol>
              <p>
                Reactivation requests are reviewed on a case-by-case basis, and
                Plaintr is under no obligation to restore terminated Accounts.
              </p>
              <h4>9.5. Survival of Terms</h4>
              <p></p>
              <ol>
                <li>Payment Obligations</li>
                <li>Indemnity Clauses</li>
                <li>Limitations of Liability</li>
                <li>Dispute Resolution and Arbitration</li>
                <li>Intellectual Property Rights</li>
              </ol>
              <h3>10. Privacy Policy and Data Protection</h3>
              <h4>10.1 Privacy Commitment</h4>
              <p>
                Plaintr is committed to protecting the privacy and personal
                information of its Users and Pros. By using Plaintr Services,
                Users agree to the collection, use, and disclosure of their data
                in accordance with Plaintr’s Privacy Policy. The Privacy Policy
                is incorporated into these Terms by reference.
              </p>
              <h4>10.2 Data Collection</h4>
              <p>
                Plaintr collects and processes User data to provide its
                services, improve platform functionality, and ensure compliance
                with legal and regulatory requirements. The types of data
                collected may include:
              </p>
              <ol>
                <li>
                  <strong>Personal Information:</strong> Name, email address,
                  phone number, and billing details.
                </li>
                <li>
                  <strong>Professional Information (Pros):</strong> License or
                  certification details, practice area, and contact information.
                </li>
                <li>
                  <strong>Usage Data:</strong> Device information, IP address,
                  and platform activity logs.
                </li>
              </ol>
              <p>
                All data collection complies with applicable privacy laws,
                including the California Consumer Privacy Act (CCPA).
              </p>
              <h4>10.3. Use of Data</h4>
              <p></p>
              <ol>
                <li>
                  <strong>Service Facilitation:</strong> To connect Users with
                  Tax Professionals.
                </li>
                <li>
                  <strong>Account Management:</strong> To process payments and
                  manage User Accounts.
                </li>
                <li>
                  <strong>Platform Improvement:</strong> To enhance platform
                  functionality and user experience.
                </li>
                <li>
                  <strong>Communication:</strong> To send updates,
                  notifications, and service-related messages.
                </li>
                <li>
                  <strong>Compliance:</strong> To meet legal and regulatory
                  obligations.
                </li>
              </ol>
              <p>Plaintr does not sell or rent User data to third parties.</p>
              <h4>10.4. Data Sharing</h4>
              <p>
                Plaintr may share User data with third parties under the
                following circumstances:
              </p>
              <ol>
                <li>
                  <strong>Service Providers:</strong> Payment processors, email
                  communication platforms, and other entities essential for
                  platform operations.
                </li>
                <li>
                  <strong>Legal Compliance:</strong> In response to lawful
                  requests, such as subpoenas or court orders.
                </li>
                <li>
                  <strong>Fraud Prevention:</strong> With authorities or
                  partners to detect, prevent, or address fraudulent or illegal
                  activities.
                </li>
              </ol>
              <p>
                Shared data is limited to what is necessary for the specified
                purpose.
              </p>
              <h4>10.5. User Consent</h4>
              <p>By using Plaintr Services, Users consent to:</p>
              <ol>
                <li>
                  <strong>Data Use:</strong> The collection, use, and sharing of
                  their data as described in these Terms and the Privacy Policy.
                </li>
                <li>
                  <strong>Communications:</strong> Receiving service-related
                  communications, including updates and reminders.
                </li>
              </ol>
              <p>
                Users can withdraw consent for specific communications, by
                emailing{" "}
                <a href="mailto:contact@Plaintr.com">contact@Plaintr.com</a>.
              </p>
              <h4>10.6. Data Security</h4>
              <p>
                Plaintr employs industry-standard practices to safeguard User
                data within our control. This includes:
              </p>
              <ol>
                <li>
                  <strong>Secure Payment Processing:</strong> All payment
                  information is handled by our third-party Payment Processor,
                  which manages sensitive data through its own security
                  protocols. Plaintr does not store or directly process Users’
                  payment card details.
                </li>
                <li>
                  <strong>Access Controls:</strong> We grant access to User data
                  only to authorized personnel who require it for legitimate
                  business purposes.
                </li>
                <li>
                  <strong>Monitoring:</strong> Our systems are monitored on an
                  ongoing basis to identify and address potential security
                  threats or vulnerabilities promptly.
                </li>
              </ol>
              <p>
                Although we strive to protect all User data, no method of
                transmission or storage can be guaranteed to be fully secure. By
                using the Plaintr Services, you acknowledge and accept that
                Plaintr cannot always ensure absolute security of data.
              </p>
              <h4>10.7. User Privacy Rights</h4>
              <p>
                Depending on applicable law, Users may have the right to request
                access to, correction of, deletion of, or a copy of certain
                personal information maintained by Plaintr. These rights are
                subject to identity-verification requirements and any
                limitations or exceptions provided by applicable law.
              </p>
              <p>
                Requests for Account deletion are governed by Section 9.1 of
                these Terms and the Account and Data Deletion provisions of the
                Privacy Policy. Users may submit privacy-related requests by
                contacting Plaintr at{" "}
                <a href="mailto:contact@Plaintr.com">contact@Plaintr.com</a>.
              </p>
              <p>
                Users can exercise these rights by contacting
                privacy@Plaintr.com.
              </p>
              <h4>10.8. Data Retention</h4>
              <p>
                Plaintr retains tax return information and related records for
                the periods required by applicable IRS and other legal
                recordkeeping requirements, or as needed to provide the
                Services. Other User information may be retained for as long as
                necessary to fulfill the purposes described in these Terms and
                the Privacy Policy, unless a longer retention period is required
                or permitted by law.
              </p>
              <p>
                Suspension or administrative termination does not alter
                applicable retention requirements. When an applicable retention
                period expires, tax return information and related records
                associated with an approved Account deletion request will be
                permanently deleted, unless further retention is required or
                permitted by law.
              </p>
              <h4>International Data Transfers</h4>
              <p>
                Plaintr’s services are intended for Users within the United
                States. If data is transferred outside the U.S., Plaintr ensures
                compliance with applicable data protection laws.
              </p>
              <h4>10.9. Updates to Privacy Policy</h4>
              <p>
                Plaintr reserves the right to update its Privacy Policy to
                reflect changes in legal requirements or platform operations.
                Users will be notified of material changes through email or
                platform notifications. Continued use of the platform after
                updates constitutes acceptance of the revised Privacy Policy.
              </p>
              <h3>11. Miscellaneous Provisions</h3>
              <h4>11.1. Entire Agreement</h4>
              <p>
                These Terms, together with the Privacy Policy and any
                supplemental terms explicitly incorporated herein, constitute
                the entire agreement between Users and Plaintr concerning the
                use of the Plaintr Services. They supersede all prior
                agreements, communications, and understandings, whether written
                or oral.
              </p>
              <h4>11.2. Amendments</h4>
              <p>
                Plaintr reserves the right to amend these Terms at its
                discretion. Material changes will be communicated to Users via
                email or in-app notifications at least fourteen (14) days before
                implementation. Continued use of the platform after such updates
                constitutes acceptance of the revised Terms. Users who do not
                agree to the amended Terms must discontinue using the Plaintr
                Services immediately.
              </p>
              <h4>11.3. Severability</h4>
              <p>
                If any provision of these Terms is found to be invalid, illegal,
                or unenforceable, the remaining provisions shall continue to be
                fully enforceable. The invalid provision will be modified to the
                extent permitted by law to achieve the original intent of the
                parties.
              </p>
              <h4>11.4. No Waiver</h4>
              <p>
                Plaintr’s failure to enforce any provision of these Terms does
                not constitute a waiver of its right to enforce that provision
                or any other provision in the future.
              </p>
              <h4>11.5. Assignment</h4>
              <p>
                Users may not assign or transfer their rights or obligations
                under these Terms without Plaintr’s prior written consent.
                Plaintr may assign or transfer its rights and obligations
                without restriction, including in connection with a merger,
                acquisition, or sale of assets.
              </p>
              <h4>11.6. Governing Law</h4>
              <p>
                These Terms are governed by and construed in accordance with the
                laws of the State of New York, without regard to conflict of law
                principles. Users agree to submit to the exclusive jurisdiction
                of the state and federal courts located in New York, NY, for the
                resolution of any disputes arising from these Terms, except
                where arbitration applies.
              </p>
              <h4>11.7. Force Majeure</h4>
              <p>
                Plaintr shall not be held liable for any delay or failure to
                perform resulting from causes outside its reasonable control,
                including but not limited to acts of God, labor disputes,
                government actions, internet or telecommunications failures,
                natural disasters, or other unforeseen events.
              </p>
              <h4>11.8. Notices</h4>
              <p>
                All notices or communications required under these Terms shall
                be delivered as follows:
              </p>
              <ol>
                <li>
                  To Users: Via email to the address associated with their
                  Account or through in-app notifications.
                </li>
                <li>
                  To Plaintr: Via email at{" "}
                  <a href="mailto:contact@Plaintr.com">contact@Plaintr.com</a>
                  or certified mail to: Plaintr LLC{" "}
                  <strong>228 Park Ave S #554715, New York, NY 10003</strong>
                </li>
              </ol>
              <p>Notices are considered received:</p>
              <ol>
                <li>Email: Upon email delivery confirmation.</li>
                <li>
                  Certified Mail: Five (5) business days after mailing if sent
                  via certified mail.
                </li>
              </ol>
              <h4>11.9. Third-Party Beneficiaries</h4>
              <p>
                These Terms do not create any third-party beneficiary rights.
                The agreement is solely between the User and Plaintr.
              </p>
              <h4>11.10. Survival</h4>
              <p>
                Provisions that by their nature should survive termination of
                these Terms shall remain in effect. These include, but are not
                limited to:
              </p>
              <ol>
                <li>Payment Obligations</li>
                <li>Indemnity Clauses</li>
                <li>Limitations of Liability</li>
                <li>Dispute Resolution and Arbitration</li>
                <li>Intellectual Property Rights</li>
              </ol>
              <h4>11.11. Contact Information</h4>
              <p>
                For any questions, concerns, or disputes regarding these Terms,
                Users may contact Plaintr at:
              </p>
              <ol>
                <li>
                  Email:{" "}
                  <a href="mailto:contact@Plaintr.com">contact@Plaintr.com</a>
                </li>
                <li>
                  <strong>228 Park Ave S #554715 New York, NY 10003</strong>
                </li>
              </ol>
              <h4>11.12. Recordkeeping</h4>
              <p>
                Plaintr will retain complete dispute and refund records for at
                least five (5) years. Records may be reviewed by regulatory
                authorities if required.
              </p>
              <h4>11.13. Compliance</h4>
              <p>
                This policy is designed to comply with IRS Circular 230, FTC
                service fairness rules, and state-level requirements in New
                York, California, and Florida. Where state law grants additional
                consumer rights, Plaintr will honor them.
              </p>
              <h4>11.14. Intellectual Property Rights</h4>
              <p>
                All work products delivered to Clients via Plaintr are deemed
                “work made for hire.” Upon full payment, all rights transfer to
                the Client, except for any pre-existing intellectual property of
                the Pro.
              </p>
              <h4>11.15. Indemnification</h4>
              <p>
                Pros agree to indemnify, defend, and hold harmless Plaintr, its
                affiliates, officers, and employees from any claims, damages,
                liabilities, or expenses arising from:
              </p>
              <p>(a) Pro’s breach of these Terms or the Service Agreement;</p>
              <p>(b) negligence or misconduct; or</p>
              <p>(c) violation of any applicable law or regulation. </p>
            </div>
          )}
        </div>
      </main>
    </Fragment>
  );
}

export default TermsConditions;
